import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { ZakresTrafienia } from "@shared/powtarzaneSlowa.js";
import { DialogMowcy } from "../components/analiza/DialogMowcy.js";
import { PodzialTekstu } from "../components/analiza/PodzialTekstu.js";
import { OknoPonownejOceny } from "../components/ocena/OknoPonownejOceny.js";
import { KartaKosztu } from "../components/koszty/KartaKosztu.js";
import { DialogLinkuRecenzji } from "../components/recenzje/DialogLinkuRecenzji.js";
import { OcenaMetodyki } from "../components/transcript/OcenaMetodyki.js";
import { mmss } from "../components/transcript/czas.js";
import { Rozmowa } from "../components/transcript/Rozmowa.js";
import { AudioPlayer } from "../components/ui/AudioPlayer.js";
import { Avatar } from "../components/ui/Avatar.js";
import { Badge } from "../components/ui/Badge.js";
import { Banner, type WariantBanera } from "../components/ui/Banner.js";
import { Button } from "../components/ui/Button.js";
import { Card } from "../components/ui/Card.js";
import { KpiWartosc } from "../components/ui/KpiTile.js";
import { Pipeline, type EtapPipeline } from "../components/ui/Pipeline.js";
import { ProgressBar } from "../components/ui/ProgressBar.js";
import { useToast } from "../components/ui/Toast.js";
import { StanBledu, StanLadowania, StanPusty } from "../components/ui/stany.js";
import { useKonto } from "../hooks/useKonto.js";
import { useSesja } from "../hooks/useSesja.js";
import type { MowcaDto, SessionDetail, UtteranceDto } from "../lib/api.js";
import { odczytajNiepelnaOceneBrzmienia, opisNiepelnejOcenyBrzmienia } from "../lib/jakoscOceny.js";
import { etykietaRoli } from "../lib/podzial.js";
import { powtarzaneSlowaSesji } from "../lib/powtarzaneSlowa.js";
import { policzPodswietlenia, zbudujRaport } from "../lib/raportOceny.js";
import {
  czekaNaCzlowieka,
  etykietaStatusu,
  etykietaToru,
  IN_PROGRESS,
  krokiPipeline,
  postepPipeline,
  rodzajToru,
} from "../lib/sessionStatus.js";
import { miaryUczestnika, zlozUczestnikow } from "../lib/uczestnicy.js";
import { uzupelnijBrakiDlaUzytkownika } from "../lib/widokUzytkownika.js";

/** Ile wypowiedzi dostaje kazdy z czterech tintow tozsamosci (--avatar-1..4). */
const LICZBA_TINTOW = 4;

/** Podpowiedz przy nieudanym podziale tekstu dla konta bez roli administratora (serwer nie podaje mu zadnego powodu). */
const PODPOWIEDZ_PODZIALU_UZYTKOWNIKA =
  "Nie udało się podzielić tekstu. Wgraj go jeszcze raz, najlepiej z dokładniejszym opisem rozmowy.";

/**
 * Czy szczegol sesji mowi, ze to rozmowa konta uzytkownika (wtedy "Wyslij do recenzji" jest wygaszony). Pole ownerUsername
 * dostaje tylko administrator (u konta user wycina je serwer). Napis to konto, null to rozmowa administratora, jak
 * w shared/wlascicielRozmowy.ts. Brak pola to "nie wiadomo": przycisk zostaje, a ostatnie slowo ma serwer
 * (400 rozmowa_konta_uzytkownika przy tworzeniu linku).
 */
function czyRozmowaKontaWSzczegole(sesja: Pick<SessionDetail, "ownerUsername">): boolean {
  return typeof sesja.ownerUsername === "string";
}

function dataPolska(iso: string): string | null {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return null;
  return new Intl.DateTimeFormat("pl-PL", { dateStyle: "long", timeStyle: "short" }).format(data);
}

/**
 * Wypowiedz, ktora leci w tej chwili z nagrania. Ostatnia, ktora zdazyla sie
 * zaczac - granica konca bywa pusta (endMs), wiec nie da sie na niej polegac.
 */
function indeksDlaCzasu(utterances: readonly UtteranceDto[], ms: number): number | null {
  let biezacy: number | null = null;
  for (const u of utterances) {
    if (u.startMs > ms) break;
    biezacy = u.index;
  }
  return biezacy;
}

export function SessionPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  // Przejsciowy blad sieci nie moze zamienic ekranu na "nie ma takiej analizy" -
  // odpytywanie i tak ponawia, wiec wystarczy baner stanu (BN-1). `odswiez` pobiera sesje
  // od razu: tak widok reaguje na wlasne akcje (zatwierdzenie podzialu, zmiana nazwy mowcy),
  // bo odpytywanie zatrzymuje sie na DONE i na SPLIT_REVIEW.
  const { session: surowaSesja, missing, glitch, odswiez } = useSesja(id);
  const [biezacyIndex, setBiezacyIndex] = useState<number | null>(null);
  const [edytowanyMowca, setEdytowanyMowca] = useState<MowcaDto | null>(null);
  const [oknoOcenyOtwarte, setOknoOcenyOtwarte] = useState(false);
  const [oknoRecenzjiOtwarte, setOknoRecenzjiOtwarte] = useState(false);
  // Konto `user` widzi uproszczony ekran: bez nazw modeli, kosztow, pomiarow i akcji platnych. Tylko administrator
  // ma "Wyslij do recenzji", "Ocen ponownie" i zmiane nazw mowcow.
  const { czyAdmin } = useKonto();
  // Odpowiedz dla usera nie ma pol technicznych przebiegow (model, dostawca): dopelniamy je, zanim sesja trafi do raportu.
  const session = useMemo(
    () => (surowaSesja && !czyAdmin ? uzupelnijBrakiDlaUzytkownika(surowaSesja) : surowaSesja),
    [surowaSesja, czyAdmin],
  );
  /**
   * Prosba z karty oceny o pokazanie wypowiedzi w transkrypcie; `token` rosnie przy kazdej prosbie. `trafienia` niesie
   * tylko lista "Powtarzane slowa": miejsca slowa do zaznaczenia w dymku.
   */
  const [skokDo, setSkokDo] = useState<{
    index: number;
    token: number;
    trafienia?: readonly ZakresTrafienia[];
  } | null>(null);

  const toast = useToast();
  const audioWrapRef = useRef<HTMLDivElement>(null);
  const utterancesRef = useRef<readonly UtteranceDto[]>([]);
  const biezacyRef = useRef<number | null>(null);
  const poprzedniStatusRef = useRef<string | null>(null);

  utterancesRef.current = session?.utterances ?? [];

  // PR-4: koniec dlugiego zadania potwierdzamy toastem. Bez tego znikniecie karty
  // "Przebieg analizy" (a ona ma zniknac) bylo by jedynym sygnalem, ze cos sie stalo.
  useEffect(() => {
    const teraz = session?.status ?? null;
    const przed = poprzedniStatusRef.current;
    poprzedniStatusRef.current = teraz;
    if (teraz === "DONE" && przed !== null && IN_PROGRESS.has(przed)) {
      toast.sukces("Analiza gotowa.");
    }
  }, [session?.status, toast]);

  const audio = session?.audio ?? null;
  const odtwarzalne = audio !== null && audio.deletedAt === null;

  /*
   * Odtwarzacz jest prymitywem F3 i nie wystawia ani pozycji odtwarzania, ani
   * przewijania - a transkrypt potrzebuje obu. Zamiast poprawiac prymityw (PB-5)
   * siegamy po jego element <audio> z zewnatrz: to jedna linijka w widoku zamiast
   * zmiany w zamrozonym komponencie. Brak API zglaszamy orkiestratorowi.
   */
  const elementAudio = useCallback((): HTMLAudioElement | null => {
    return audioWrapRef.current?.querySelector("audio") ?? null;
  }, []);

  useEffect(() => {
    if (!odtwarzalne) {
      setBiezacyIndex(null);
      biezacyRef.current = null;
      return;
    }
    const el = elementAudio();
    if (!el) return;

    const naCzas = (): void => {
      const nowy = indeksDlaCzasu(utterancesRef.current, el.currentTime * 1000);
      if (nowy === biezacyRef.current) return;
      biezacyRef.current = nowy;
      setBiezacyIndex(nowy);
    };

    el.addEventListener("timeupdate", naCzas);
    return () => el.removeEventListener("timeupdate", naCzas);
  }, [odtwarzalne, elementAudio]);

  /*
   * Przewijamy, ale NIE wlaczamy odtwarzania. Wlasny stan odtwarzacza (ikona
   * play/pauza) zna tylko on sam - gdybysmy zagrali za jego plecami, przycisk
   * pokazywalby "odtwarzaj" przy grajacym nagraniu.
   */
  const przewin = useCallback(
    (ms: number) => {
      const el = elementAudio();
      if (!el) return;
      el.currentTime = ms / 1000;
    },
    [elementAudio],
  );

  /** Tint po nazwie mowcy: transkrypt zna tylko nazwy. Kolejnosc nadaja mowcy sesji, wiec nazwa nie tasuje kolorow. */
  const tintDlaMowcy = useMemo(() => {
    const mapa = new Map<string, number>();
    const dodaj = (nazwa: string): void => {
      if (!mapa.has(nazwa)) mapa.set(nazwa, (mapa.size % LICZBA_TINTOW) + 1);
    };
    session?.speakers.forEach((s) => dodaj(s.displayName ?? s.label));
    session?.utterances.forEach((u) => dodaj(u.speaker));
    return (nazwa: string): number => mapa.get(nazwa) ?? 1;
  }, [session]);

  /** Tint po id mowcy: ekran zatwierdzania podzialu zmienia nazwy w locie, a kolory maja zostac. */
  const tintDlaId = useMemo(() => {
    const mapa = new Map<string, number>();
    session?.speakers.forEach((s, i) => mapa.set(s.id, (i % LICZBA_TINTOW) + 1));
    return (idMowcy: string): number => mapa.get(idMowcy) ?? 1;
  }, [session]);

  /*
   * Podswietlenia cytatow, jakosc (kolor) wypowiedzi i wskazania przy dymkach. Cytat jest podkreslony, a dymek
   * barwiony WYLACZNIE wtedy, gdy cytat zostal zweryfikowany w kodzie i miesci sie w tekscie wypowiedzi
   * (lib/raportOceny.ts, policzPodswietlenia). Wskazanie z cytatem, ktorego nie znaleziono, zostaje w karcie
   * oceny i przy dymku z plakietka "cytat niezweryfikowany", bez podkreslenia, litery i koloru.
   */
  const podswietlenia = useMemo(
    () => (session ? policzPodswietlenia(session.utterances, session.lensRuns) : null),
    [session],
  );

  /** Raport z oceny jako dane: stan karty, parametry, mowcy ze wskazaniami i cwiczeniami. */
  const raport = useMemo(() => (session ? zbudujRaport(session) : null), [session]);

  /**
   * "Powtarzane slowa" w karcie oceny: liczenie slow z listy metodyki na wypowiedziach, ktore strona juz ma (bez modelu
   * i bez zapytania do serwera). Tylko administrator: konto `user` tej sekcji nie widzi.
   */
  const powtarzaneSlowa = useMemo(
    () => (session && czyAdmin ? powtarzaneSlowaSesji(session.utterances) : undefined),
    [session, czyAdmin],
  );

  const pokazWypowiedz = useCallback((index: number, trafienia?: readonly ZakresTrafienia[]) => {
    setSkokDo((poprzednie) => ({ index, token: (poprzednie?.token ?? 0) + 1, trafienia }));
  }, []);

  if (missing) {
    return (
      <>
        <h1 className="page-title">Nie ma takiej analizy</h1>
        <p className="page-lead">Adres jest nieaktualny albo analiza została usunięta.</p>
        <Card>
          <StanPusty
            tytul="Ta analiza nie istnieje"
            opis="Sprawdź adres albo wróć do listy analiz."
            akcja={
              <Link className="btn btn--secondary" to="/">
                Wróć do analiz
              </Link>
            }
          />
        </Card>
      </>
    );
  }

  if (!session || !raport || !podswietlenia) {
    return (
      <>
        <h1 className="page-title">Analiza</h1>
        <p className="page-lead">Wczytuję szczegóły rozmowy.</p>
        {/* Informacja, nie ostrzezenie: na tej stronie bursztyn to zloto oceny, czyli "najlepsze" (jak w WynikPage). Pilny,
            bo wczesniej byl ostrzezeniem: czytnik oglasza go od razu, tak jak banery o nieudanym odswiezeniu listy. */}
        {glitch && <Banner wariant="info" pilny tekst="Chwilowy problem z połączeniem - ponawiam próbę." />}
        <Card>
          <StanLadowania tekst="Wczytuję analizę..." />
        </Card>
      </>
    );
  }

  const jestTekst = session.inputKind === "TEXT";
  // BN-3: jeden baner naraz, a niepelna ocena brzmienia bije objasnienie plakietek. Oba ida banerem informacji, nie
  // ostrzezenia: baner stoi w karcie "Rozmowa" tuz nad dymkami, a tam bursztyn to zloto oceny, czyli "najlepsze" (decyzja
  // autora metodyki z 29.10.2026). Wariant jest ten sam, wiec pierwszenstwo daje warunek ponizej, a nie kolor. Niepelna
  // ocena byla ostrzezeniem i zostaje pilna dla czytnika, objasnienie plakietek czeka na przerwe.
  const niepelnaOcena = odczytajNiepelnaOceneBrzmienia(session.healthFlags);
  // Plakietki pomiaru i ocena brzmienia to widok administratora: user ich nie ma, wiec nie ma tez banera o nich.
  const baner: { wariant: WariantBanera; tekst: string; pilny: boolean } | null = !czyAdmin
    ? null
    : niepelnaOcena
      ? { wariant: "info", tekst: opisNiepelnejOcenyBrzmienia(niepelnaOcena), pilny: true }
      : {
          wariant: "info",
          tekst:
            "Plakietki przy wypowiedzi mają dwie postacie: pomiar z sygnału podajemy jako wartość, a ocenę modelu z jawnym podpisem. Brak plakietki znaczy, że nagranie nie dało podstaw do jej wystawienia.",
          pilny: false,
        };
  const running = IN_PROGRESS.has(session.status);
  const failed = session.status === "FAILED";
  const brakMowy = session.status === "NO_SPEECH";
  const czekaNaZatwierdzenie = czekaNaCzlowieka(session.status);
  const dzieliTekst = session.status === "SPLITTING";
  const maWypowiedzi = session.utterances.length > 0;
  /** Legenda kolorow ma sens, gdy jakis parametr sie policzyl: tylko wtedy kolory moga z czegos wynikac. */
  const pokazLegende = raport.parametry.some(
    (p) => p.stan.rodzaj === "oceniono" || p.stan.rodzaj === "brak-wskazan",
  );

  const tor = rodzajToru({
    inputKind: session.inputKind,
    status: session.status,
    speakersAssignedBy: session.speakersAssignedBy,
    maWypowiedzi,
  });
  const postep = postepPipeline(session.status, tor);
  // Porazka podzialu: tekst, ktory nie doszedl do wypowiedzi. Powod jest gotowym zdaniem po polsku.
  const porazkaPodzialu = failed && jestTekst && !maWypowiedzi;

  const etapy: EtapPipeline[] = krokiPipeline(session.status, tor).map((k) => ({
    etykieta: k.etykieta,
    status: k.stan ?? "oczekuje",
  }));

  const dataUsuniecia = audio?.deletedAt ? dataPolska(audio.deletedAt) : null;
  const dataUtworzenia = dataPolska(session.createdAt);
  // Dlugosc tylko dla nagrania i tylko gdy jest znana: brak albo 0 to "nie wiemy", nie "0:00".
  const czasNagrania =
    !jestTekst && session.durationSec !== null && session.durationSec > 0 ? mmss(session.durationSec * 1000) : null;
  const uczestnicy = zlozUczestnikow(session.speakers, session.summary.speakers);
  const podstawaSlow = session.summary.basis === "words";
  /** Czy mowcy sa juz znani (tekst w trakcie podzialu albo nagranie przed transkrypcja ich jeszcze nie ma). */
  const opisBrakuUczestnikow = !running
    ? jestTekst
      ? "Ta analiza nie ma żadnych mówców."
      : "W tym nagraniu nie rozpoznaliśmy żadnego mówcy."
    : jestTekst
      ? "Mówcy pojawią się tutaj, gdy skończy się podział tekstu na wypowiedzi."
      : "Mówcy pojawią się tutaj, gdy skończy się rozpoznawanie mowy.";

  return (
    <>
      <h1 className="page-title">{session.title}</h1>
      <p className="page-lead">
        {etykietaStatusu(session.status)}
        {jestTekst && " - analiza tekstu"}
        {/* Analizy o tej samej nazwie (np. "Rozmowa (tekst)") rozni dopiero data wgrania; dlugosc
            nagrania dopelnia obraz. Czasu PRZETWARZANIA nie pokazujemy: API nie zwraca ani znacznika
            konca, ani czasu ostatniej zmiany sesji, a liczenie go z innych pol byloby zgadywaniem. */}
        {dataUtworzenia && ` - utworzona ${dataUtworzenia}`}
        {czasNagrania && ` - nagranie: ${czasNagrania}`}
        {czyAdmin && session.sttModel && ` - transkrypcja: ${session.sttModel}`}
        {czyAdmin && session.expressionModel && ` - brzmienie: ${session.expressionModel}`}
        {czyAdmin &&
          !jestTekst &&
          session.expressionProvider === null &&
          session.status === "DONE" &&
          " - bez oceny brzmienia"}
      </p>

      {/* BN-2: baner stanu stoi POD naglowkiem strony i NAD kartami. Wczesniej byl
          komponentem bledu POLA (.form-error) uzytym jako baner strony, w dodatku
          nad <h1>. Informacja, nie ostrzezenie: na tej stronie bursztyn to zloto oceny, czyli "najlepsze". Pilnosc dla
          czytnika zostaje ta sama co przy ostrzezeniu (pilny). */}
      {glitch && <Banner wariant="info" pilny tekst="Chwilowy problem z połączeniem - ponawiam próbę." />}

      {/* PR-4: po DONE karta postepu ZNIKA. Po FAILED zostaje, bo niesie powod. */}
      {czyAdmin && (running || failed) && (
        <Card tytul="Przebieg analizy">
          <Pipeline etapy={etapy} etykieta={etykietaToru(tor)} />
          {/* Przy czekaniu na zatwierdzenie nic sie nie liczy, wiec pasek postepu klamalby:
              zamiast niego tor pokazuje biezacy krok ("Zatwierdzenie podziału"). */}
          {running && !czekaNaZatwierdzenie && (
            <ProgressBar
              etykieta={etykietaStatusu(session.status)}
              procent={(postep.zrobione / postep.wszystkie) * 100}
            />
          )}
          {failed && (
            <StanBledu
              tytul={porazkaPodzialu ? "Nie udało się podzielić tekstu na wypowiedzi" : "Analiza się nie udała"}
              opis={
                porazkaPodzialu
                  ? (session.error ??
                    "Model nie zdołał podzielić tekstu. Wgraj go jeszcze raz, najlepiej z dokładniejszym opisem rozmowy.")
                  : jestTekst
                    ? "Tekst został przyjęty, ale przetwarzanie przerwał błąd. Spróbuj wgrać go jeszcze raz."
                    : "Nagranie zostało przyjęte, ale przetwarzanie przerwał błąd. Spróbuj wgrać je jeszcze raz."
              }
              szczegol={porazkaPodzialu ? undefined : (session.error ?? undefined)}
              akcja={
                <Link className="btn btn--secondary" to="/">
                  Wróć do analiz
                </Link>
              }
            />
          )}
        </Card>
      )}

      {/* Uproszczony widok (user): bez toru etapow pipeline'u i bez technicznego szczegolu bledu. Zostaje jedno zdanie
          o tym, ze analiza trwa albo sie nie udala. Czekanie na zatwierdzenie podzialu ma wlasna karte nizej. */}
      {!czyAdmin && ((running && !czekaNaZatwierdzenie) || failed) && (
        <Card tytul="Analiza">
          {running && !czekaNaZatwierdzenie && (
            <ProgressBar etykieta="Analizujemy rozmowę" procent={(postep.zrobione / postep.wszystkie) * 100} />
          )}
          {failed && (
            <StanBledu
              tytul={porazkaPodzialu ? "Nie udało się podzielić tekstu na wypowiedzi" : "Analiza się nie udała"}
              opis={
                porazkaPodzialu
                  ? // Serwer podmienia KAZDY tekst bledu dla tego konta na jedno ogolne zdanie ("Analiza nie powiodla sie."),
                    // wiec session.error nie niesie tu zadnej wskazowki. Zawsze stala podpowiedz.
                    PODPOWIEDZ_PODZIALU_UZYTKOWNIKA
                  : jestTekst
                    ? "Tekst został przyjęty, ale przetwarzanie przerwał błąd. Spróbuj wgrać go jeszcze raz."
                    : "Nagranie zostało przyjęte, ale przetwarzanie przerwał błąd. Spróbuj wgrać je jeszcze raz."
              }
              akcja={
                <Link className="btn btn--secondary" to="/">
                  Wróć do analiz
                </Link>
              }
            />
          )}
        </Card>
      )}

      {/* WY-3: odtwarzacz stoi NAD transkryptem, bo to transkrypt nim steruje.
          Element <audio> siedzi w galezi, ktora odpytywanie zostawia nietknieta -
          gdyby wchodzil i wychodzil z drzewa, odtwarzanie restartowaloby sie co
          odswiezenie. */}
      {/* Tekst nie ma nagrania, wiec karta mowi o tym tylko wtedy, gdy analiza jest skonczona. W trakcie
          (zwlaszcza przy zatwierdzaniu podzialu) zajmowalaby miejsce nad tym, co trzeba zrobic. */}
      {!(jestTekst && (running || failed)) && (
        <Card tytul="Nagranie">
          {audio === null ? (
            jestTekst ? (
              <StanPusty
                tytul="Brak nagrania"
                opis="Ta analiza powstała z tekstu, więc nie ma pliku dźwiękowego. Nie mierzymy też tempa, tonu ani pauz."
              />
            ) : (
              <StanPusty
                tytul="Nie ma nagrania"
                opis="Do tej analizy nie zapisano pliku dźwiękowego. Transkrypt i pomiary są kompletne."
              />
            )
          ) : audio.deletedAt !== null ? (
            <StanPusty
              tytul="Nagranie usunięte zgodnie z retencją"
              opis={
                dataUsuniecia === null
                  ? "Plik skasowaliśmy po zakończeniu analizy. Transkrypt i pomiary zostają."
                  : `Plik skasowaliśmy ${dataUsuniecia}, zgodnie z ustawieniem retencji. Transkrypt i pomiary zostają.`
              }
            />
          ) : (
            <div ref={audioWrapRef}>
              <AudioPlayer zrodlo={`/api/sessions/${session.id}/audio`} etykieta={session.title} />
              <p className="metryka__podpowiedz">
                Kliknięcie znacznika czasu przy wypowiedzi przewija nagranie do tego miejsca.
              </p>
            </div>
          )}
        </Card>
      )}

      {(session.context || session.speakersAssignedBy === "AI") && (
        <Card tytul="Opis rozmowy">
          {session.context && <p className="opis-rozmowy">{session.context}</p>}
          {session.speakersAssignedBy === "AI" && (
            <p className="field-hint">
              {/* Zdanie o zatwierdzeniu jest prawdziwe dopiero po nim: kolumna ustawia sie przy zapisie
                  podzialu, a w SPLIT_REVIEW uzytkownik jeszcze niczego nie zatwierdzil. */}
              {czekaNaZatwierdzenie
                ? "Mówców przypisała AI na podstawie opisu rozmowy. Sprawdź podział poniżej i zatwierdź go."
                : "Mówców przypisała AI na podstawie opisu rozmowy i zatwierdził użytkownik."}
            </p>
          )}
        </Card>
      )}

      {czekaNaZatwierdzenie ? (
        <PodzialTekstu sesja={session} odswiez={odswiez} tintDlaId={tintDlaId} />
      ) : (
        <>
          <Card tytul="Rozmowa">
            {/* Plakietki sa pomiarem z dzwieku i oceną brzmienia: tekst ich nie ma, wiec
                objasnienie zostaje tylko przy nagraniu. */}
            {!jestTekst && baner !== null && <Banner wariant={baner.wariant} pilny={baner.pilny} tekst={baner.tekst} />}

            {maWypowiedzi ? (
              <Rozmowa
                utterances={session.utterances}
                tintDlaMowcy={tintDlaMowcy}
                modelEkspresji={session.expressionModel}
                zakresy={podswietlenia.zakresy}
                biezacyIndex={biezacyIndex}
                naPrzewin={odtwarzalne ? przewin : null}
                dlugoscSek={session.durationSec}
                pokazCzas={session.hasTimestamps}
                rodzajMaterialu={jestTekst ? "tekst" : "nagranie"}
                jakosci={podswietlenia.jakosci}
                uwagi={podswietlenia.uwagi}
                pokazLegende={pokazLegende}
                skokDo={skokDo}
              />
            ) : brakMowy ? (
              <StanPusty
                tytul="Nie znaleziono mowy"
                opis="W tym nagraniu nie było wypowiedzi do rozpoznania. To nie jest błąd - sprawdź, czy plik zawiera rozmowę."
              />
            ) : failed ? (
              <StanPusty
                tytul={porazkaPodzialu ? "Nie ma podziału na wypowiedzi" : "Nie ma transkryptu"}
                opis={
                  czyAdmin
                    ? porazkaPodzialu
                      ? "Podział tekstu nie powiódł się. Powód opisuje karta z przebiegiem analizy."
                      : "Analiza przerwała się przed rozpoznaniem mowy. Powód opisuje karta z przebiegiem analizy."
                    : // Karty z przebiegiem analizy konto bez roli administratora nie ma; ma karte "Analiza" z jednym zdaniem.
                      porazkaPodzialu
                      ? "Podział tekstu nie powiódł się. Szczegóły podaje karta Analiza wyżej."
                      : "Analiza przerwała się przed rozpoznaniem mowy. Szczegóły podaje karta Analiza wyżej."
                }
              />
            ) : dzieliTekst ? (
              <StanLadowania tekst="Dzielę tekst na wypowiedzi..." />
            ) : running ? (
              <StanLadowania tekst={jestTekst ? "Przyjmuję tekst..." : "Rozpoznaję mowę..."} />
            ) : (
              <StanPusty
                tytul="Nie ma wypowiedzi"
                opis="Analiza zakończyła się bez rozpoznanych wypowiedzi."
              />
            )}
          </Card>

          <Card tytul="Uczestnicy">
            {uczestnicy.length > 0 ? (
              uczestnicy.map((u) => {
                // Metryki udzialu i tur to pomiar dla administratora; user widzi sama liste uczestnikow.
                const miary = czyAdmin && u.metryki ? miaryUczestnika(u.metryki, session.summary.basis) : null;
                const mowca = u.mowca;
                return (
                  <div className="uczestnik" data-tint={tintDlaMowcy(u.nazwa)} key={u.klucz}>
                    <div className="uczestnik__head">
                      <span className="msg__avatar">
                        <Avatar nazwa={u.nazwa} rozmiar="s" />
                      </span>
                      <h3 className="uczestnik__nazwa">{u.nazwa}</h3>
                      {mowca && mowca.role !== "INNY" && <Badge>{etykietaRoli(mowca.role)}</Badge>}
                      {czyAdmin && mowca && (
                        <span className="uczestnik__akcje">
                          <Button
                            wariant="secondary"
                            aria-label={`Zmień nazwę mówcy ${u.nazwa}`}
                            onClick={() => setEdytowanyMowca(mowca)}
                          >
                            Zmień nazwę
                          </Button>
                        </span>
                      )}
                    </div>
                    {miary && (
                      <div className="metryki">
                        <div>
                          <KpiWartosc
                            etykieta={miary.etykietaUdzialu}
                            wartosc={miary.udzialProcent}
                            jednostka="%"
                            druga={miary.udzialDruga ?? undefined}
                          />
                        </div>
                        <div>
                          <KpiWartosc etykieta="Tury" wartosc={miary.tury} />
                        </div>
                        <div>
                          <KpiWartosc
                            etykieta="Średnia tura"
                            wartosc={miary.sredniaTura.wartosc}
                            jednostka={miary.sredniaTura.jednostka}
                            powodBraku="Za mało danych, żeby policzyć średnią. Nie podajemy liczby, której nie mierzymy."
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <StanPusty tytul="Nie ma jeszcze uczestników" opis={opisBrakuUczestnikow} />
            )}
          </Card>
        </>
      )}

      {/* Mediana reakcji, wejscia w slowo i liczba tur to pomiary techniczne: tylko dla administratora. */}
      {czyAdmin && (
        <Card tytul="Przebieg rozmowy">
          <Banner
            wariant="info"
            tekst={
              podstawaSlow
                ? "Udział w rozmowie jest policzony ze słów w wypowiedziach. Brak nagrania, więc nie mierzymy czasów reakcji ani wejść w słowo. To pomiar, nie ocena."
                : "Liczby w tej karcie i w karcie z uczestnikami są policzone z granic wypowiedzi. To pomiar, nie ocena."
            }
          />

          {session.summary.speakers.length > 0 ? (
            <div className="metryki">
              <div>
                <KpiWartosc etykieta="Tury" wartosc={String(session.summary.turns)} />
              </div>
              {/* Czasu reakcji i wejsc w slowo nie da sie zmierzyc bez nagrania. Dla tekstu
                  nie ma tych kafelkow wcale, a nie kreski z dymkiem: to nie jest "za malo
                  danych", tylko miara, ktorej tu nie ma (patrz baner nad kafelkami). */}
              {!podstawaSlow && (
                <>
                  <div>
                    <KpiWartosc
                      etykieta="Czas reakcji (mediana)"
                      wartosc={
                        session.summary.medianResponseGapMs === null
                          ? null
                          : String(Math.round(session.summary.medianResponseGapMs))
                      }
                      jednostka="ms"
                      powodBraku="Za mało przejść między mówcami, żeby policzyć medianę. Nie podajemy liczby, której nie mierzymy."
                    />
                  </div>
                  <div>
                    <KpiWartosc
                      etykieta="Wejścia w słowo"
                      wartosc={session.summary.interruptions === null ? null : String(session.summary.interruptions)}
                      powodBraku="Wejść w słowo nie da się zmierzyć w tej analizie. Nie podajemy liczby, której nie mierzymy."
                    />
                  </div>
                </>
              )}
            </div>
          ) : (
            <StanPusty
              tytul="Nie ma czego mierzyć"
              opis={
                running
                  ? jestTekst
                    ? "Pomiary przebiegu policzymy, gdy tekst będzie podzielony na wypowiedzi."
                    : "Pomiary przebiegu policzymy, gdy skończy się rozpoznawanie mowy."
                  : "Bez rozpoznanych wypowiedzi nie ma granic, z których liczymy przebieg."
              }
            />
          )}
        </Card>
      )}

      <OcenaMetodyki
        sesjaId={session.id}
        status={session.status}
        maWypowiedzi={maWypowiedzi}
        raport={raport}
        onOcen={czyAdmin ? () => setOknoOcenyOtwarte(true) : undefined}
        onWyslijDoRecenzji={czyAdmin ? () => setOknoRecenzjiOtwarte(true) : undefined}
        rozmowaKontaUzytkownika={czyRozmowaKontaWSzczegole(session)}
        uproszczony={!czyAdmin}
        onPokazWypowiedz={pokazWypowiedz}
        powtarzaneSlowa={powtarzaneSlowa}
      />

      {/* Koszt analizy stoi poza ternarem "czeka na zatwierdzenie": po podziale tekstu koszt juz istnieje. */}
      {czyAdmin && session.koszt && <KartaKosztu koszt={session.koszt} />}

      <p className="page-lead powrot-do-listy">
        <Link to="/">Wróć do listy analiz</Link>
      </p>

      {/* Okna akcji administratora. User ich nie dostaje: ani platnej ponownej oceny, ani zmiany nazw mowcow, ani linku. */}
      {czyAdmin && (
        <>
          <OknoPonownejOceny
            otwarty={oknoOcenyOtwarte}
            sesjaId={session.id}
            poprzedniWybor={session.ocena?.soczewki.map((s) => s.slug) ?? []}
            pierwszaOcena={raport.stan === "nie-oceniono"}
            onZamknij={() => setOknoOcenyOtwarte(false)}
            odswiez={odswiez}
          />

          <DialogMowcy
            otwarty={edytowanyMowca !== null}
            sesjaId={session.id}
            mowca={edytowanyMowca}
            pozostaliMowcy={session.speakers.filter((m) => m.id !== edytowanyMowca?.id)}
            onZamknij={() => setEdytowanyMowca(null)}
            onZapisano={odswiez}
          />

          <DialogLinkuRecenzji
            otwarty={oknoRecenzjiOtwarte}
            onZamknij={() => setOknoRecenzjiOtwarte(false)}
            biezaca={{ id: session.id, tytul: session.title, liczbaWypowiedzi: session.utterances.length }}
          />
        </>
      )}
    </>
  );
}
